import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { CampusLastVisited } from '@chancenwerk/campus-learning-content/entities/CampusLastVisited.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';

const LAST_VISITED_MAX_ENTRIES = 3;

const commitLastVisited = registerMutation({
  type: () => Boolean,
  operationName: 'commitLastVisited',
  resolve: (lessonID: string) => {
    return async () => {
      if (!lessonID || !lessonID.length) {
        throw new Error('Invalid lessonID');
      }

      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const existing = await CampusLastVisited.getByID<CampusLastVisited>(user._id)({
        userID: 1,
        lessonIDs: 1,
      });

      if (!existing) {
        await new CampusLastVisited({
          userID: user._id,
          lessonIDs: [lessonID],
        }).create();
        return;
      }

      if (!existing.lessonIDs) {
        existing.lessonIDs = [];
      }

      existing.lessonIDs = [lessonID, ...existing.lessonIDs.filter(id => id !== lessonID)];
      if (existing.lessonIDs.length > LAST_VISITED_MAX_ENTRIES) {
        existing.lessonIDs = existing.lessonIDs.slice(0, LAST_VISITED_MAX_ENTRIES);
      }

      await existing.save();

      return true;
    };
  },
  options: {},
  params: [{ type: () => String, name: 'lessonID' }],
});

const getLastVisited = registerQuery({
  type: () => CampusLastVisited,
  operationName: 'getLastVisited',
  resolve: () => {
    return async (gqlFields: BaseQLSelectionSet<CampusLastVisited>) => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const existing = await CampusLastVisited.getByID<CampusLastVisited>(user._id)({
        userID: 1,
        lessonIDs: 1,
        ...gqlFields,
      });

      if (!existing) {
        const n = new CampusLastVisited({
          userID: user._id,
          lessonIDs: [],
          lessons: [],
        });
        await n.create();
        return n;
      }

      return existing;
    };
  },
  options: {},
  params: [],
});

export namespace LastVisitedAPI {
  export const commit = commitLastVisited;
  export const get = getLastVisited;
}
